/**
 * DeepBI Chat Widget - Standalone 入口
 * 
 * 用于在纯 HTML 页面中通过 <script> 标签引入使用。
 * 打包后会将 React/ReactDOM 及所有依赖都包含在内，
 * 无需宿主页面安装任何 npm 包。
 * 
 * 用法：
 *   <link rel="stylesheet" href="deepbi-chat.standalone.css">
 *   <script src="deepbi-chat.standalone.js"></script>
 *   <script>
 *     DeepBIChat.init({
 *       apiEndpoint: 'https://xxx.deepbi.com/api/data',
 *       apiKey: 'your-api-key',
 *       brandColor: '#261D1E',
 *     });
 *   </script>
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import ChatWidget from './widget/ChatWidget';
import { ChatWidgetProps, ChatWidgetRef } from './widget/types';
import './style.scss';

// 全局实例管理
let _root: ReactDOM.Root | null = null;
let _container: HTMLElement | null = null;
let _widgetRef: ChatWidgetRef | null = null;

/**
 * 初始化聊天组件
 * @param props - ChatWidget 的所有 props（apiEndpoint 和 apiKey 为必填）
 * @param container - 可选，指定挂载的 DOM 容器；不传则自动创建一个 div 挂到 body 上
 * @returns 控制接口对象 { open, close, sendMessage, getSessionInfo, destroy }
 */
function init(
  props: ChatWidgetProps,
  container?: HTMLElement | string
): {
  open: () => void;
  close: () => void;
  sendMessage: (message: string) => void;
  getSessionInfo: () => ReturnType<ChatWidgetRef['getSessionInfo']> | null;
  resetSession: () => void;
  destroy: () => void;
} {
  // 如果已经初始化过，先销毁
  if (_root) {
    destroy();
  }

  // 确定挂载容器
  if (typeof container === 'string') {
    _container = document.getElementById(container) || document.querySelector(container) as HTMLElement;
    if (!_container) {
      console.error(`[DeepBIChat] 找不到容器元素: ${container}`);
      // 回退到自动创建
      _container = null;
    }
  } else if (container instanceof HTMLElement) {
    _container = container;
  }

  if (!_container) {
    _container = document.createElement('div');
    _container.id = 'deepbi-chat-root';
    document.body.appendChild(_container);
  }

  // 创建 React Root 并渲染
  _root = ReactDOM.createRoot(_container);

  const WidgetWrapper: React.FC = () => {
    const ref = React.useRef<ChatWidgetRef>(null);

    React.useEffect(() => {
      // 保存 ref 供外部 API 使用
      if (ref.current) {
        _widgetRef = ref.current;
      }
    });

    return React.createElement(ChatWidget, {
      ...props,
      ref,
    });
  };

  _root.render(React.createElement(WidgetWrapper));

  // 返回控制 API
  return {
    open: () => _widgetRef?.openChat(),
    close: () => _widgetRef?.closeChat(),
    sendMessage: (message: string) => _widgetRef?.sendMessage(message),
    getSessionInfo: () => _widgetRef?.getSessionInfo() ?? null,
    resetSession: () => _widgetRef?.resetSession(),
    destroy,
  };
}

/**
 * 销毁聊天组件
 */
function destroy() {
  if (_root) {
    _root.unmount();
    _root = null;
  }
  // 只移除我们自动创建的容器
  if (_container && _container.id === 'deepbi-chat-root' && _container.parentNode) {
    _container.parentNode.removeChild(_container);
  }
  _container = null;
  _widgetRef = null;
}

// 暴露到全局 window 对象
const DeepBIChat = { init, destroy };

// 挂载到全局
(window as any).DeepBIChat = DeepBIChat;

export default DeepBIChat;
